import { zodResolver } from '@hookform/resolvers/zod';
import {
  payOccurrenceBodySchema,
  type AmountMode,
  type PayOccurrenceBody,
} from '@klopotun/contracts';
import type { FormEvent } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Button,
  DateField,
  SegmentedControl,
  TextField,
  type SegmentedOption,
} from '../../../../shared/ui/index.js';
import {
  isAmountQuestionAsked,
  toAmountMinor,
  toPayBodyDraft,
} from '../../lib/confirm-payment-adapter.js';
import { confirmPaymentSchema } from '../../lib/confirm-payment-schema.js';
import type { ConfirmPaymentValues } from '../../model/confirm-payment-values.js';
import styles from './confirm-payment-form.module.css';

const APPLY_AMOUNT_OPTIONS: SegmentedOption[] = [
  { value: 'once', label: 'Разово' },
  { value: 'always', label: 'Тепер завжди' },
];

type ConfirmPaymentFormProps = {
  initialValues: ConfirmPaymentValues;
  amountMode: AmountMode;
  templateAmountMinor: number | null;
  templateCurrency: string;
  onSubmit: (body: PayOccurrenceBody) => void;
  onCancel: () => void;
  busy: boolean;
};

export const ConfirmPaymentForm = ({
  initialValues,
  amountMode,
  templateAmountMinor,
  templateCurrency,
  onSubmit,
  onCancel,
  busy,
}: ConfirmPaymentFormProps) => {
  // `values` and not `defaultValues` alone: the form mounts as soon as the period is in cache,
  // which happens while a refetch of a stale entry is still in flight, and defaultValues are read
  // once and never again. The field would keep the old amount while the rest of the screen shows
  // the new one. keepDirtyValues protects whatever the person has already typed.
  const { control, handleSubmit } = useForm<ConfirmPaymentValues>({
    defaultValues: initialValues,
    values: initialValues,
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(confirmPaymentSchema),
    mode: 'onTouched',
  });
  const amount = useWatch({ control, name: 'amount' });
  const isQuestionAsked = isAmountQuestionAsked({
    amountMode,
    templateAmountMinor,
    templateCurrency,
    amountMinor: toAmountMinor(amount, initialValues.currency),
    currency: initialValues.currency,
  });

  const save = handleSubmit((values) => {
    onSubmit(payOccurrenceBodySchema.parse(toPayBodyDraft(values)));
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void save(event);
  };

  return (
    <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
      <div className={styles.fields}>
        <Controller
          control={control}
          name="amount"
          render={({ field, fieldState }) => (
            <TextField
              label="Сума"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="decimal"
              placeholder="299.00"
              hint={`Валюта: ${initialValues.currency}`}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="paidOn"
          render={({ field, fieldState }) => (
            <DateField
              label="Дата оплати"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              error={fieldState.error?.message}
            />
          )}
        />

        {isQuestionAsked && (
          <Controller
            control={control}
            name="applyAmountTo"
            render={({ field }) => (
              <SegmentedControl
                label="Сума інша, ніж очікувалась"
                name={field.name}
                options={APPLY_AMOUNT_OPTIONS}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
              />
            )}
          />
        )}
      </div>

      <div className={styles.actions}>
        <Button type="submit" busy={busy}>
          Підтвердити оплату
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>
          Скасувати
        </Button>
      </div>
    </form>
  );
};
